import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { fmtCupo } from '../utils/format';
import { toast } from 'sonner';
import { Calendar, Users, Building2, Plus, X, Check, AlertTriangle, Video, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const toTitleCase = (s) => {
  if (!s) return '';
  return s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
};

const PerfilContratista = () => {
  const { token, user } = useAuth();
  const [sesiones, setSesiones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [showInscribir, setShowInscribir] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEmpresas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras`, { headers });
      setEmpresas(res.data || []);
    } catch { }
  }, [token]);

  const fetchSesiones = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedEmpresa) params.empresa_id = selectedEmpresa;
      const res = await axios.get(`${API}/sesiones/disponibles/ese`, { headers, params });
      setSesiones(res.data);
    } catch { toast.error('Error al cargar sesiones'); }
    finally { setLoading(false); }
  }, [token, selectedEmpresa]);

  const fetchUsuarios = useCallback(async (empresaId) => {
    if (!empresaId) return;
    try {
      const res = await axios.get(`${API}/empresas-colaboradoras/${empresaId}/usuarios`, { headers });
      setUsuarios(res.data || []);
    } catch { setUsuarios([]); }
  }, [token]);

  useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);
  useEffect(() => { fetchSesiones(); }, [fetchSesiones]);
  useEffect(() => { if (selectedEmpresa) fetchUsuarios(selectedEmpresa); }, [selectedEmpresa, fetchUsuarios]);

  const handleInscribir = async () => {
    if (!selectedEmpresa || selectedWorkers.length === 0) {
      toast.error('Seleccione empresa y al menos un trabajador');
      return;
    }
    try {
      const res = await axios.post(`${API}/sesiones/inscribir`, {
        sesion_id: showInscribir.id,
        empresa_id: parseInt(selectedEmpresa),
        trabajadores: selectedWorkers.map(w => ({ rut: w.rut, nombre: w.nombre }))
      }, { headers });
      toast.success(res.data.message);
      setShowInscribir(null);
      setSelectedWorkers([]);
      fetchSesiones();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al inscribir'); }
  };

  const handleCancelar = async (inscId) => {
    setConfirmAction({
      message: '¿Cancelar esta inscripcion? El cupo sera liberado.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/sesiones/inscripcion/${inscId}`, { headers });
          toast.success('Inscripcion cancelada');
          fetchSesiones();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  const toggleWorker = (u) => {
    setSelectedWorkers(prev => {
      const exists = prev.find(w => w.rut === u.rut);
      if (exists) return prev.filter(w => w.rut !== u.rut);
      return [...prev, { rut: u.rut, nombre: u.nombre }];
    });
  };

  const groupedByDate = sesiones.reduce((acc, s) => {
    const d = s.fecha;
    if (!acc[d]) acc[d] = [];
    acc[d].push(s);
    return acc;
  }, {});
  // Ordenar fechas DESC (más futura arriba)
  const groupedEntriesDesc = Object.entries(groupedByDate).sort(([a], [b]) => b.localeCompare(a));

  return (
    <div className="space-y-6" data-testid="perfil-contratista-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-indigo-600" />
          Perfil Contratista - Calendario de Capacitaciones
        </h1>
      </div>

      {/* Empresa Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <Building2 className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Mi Empresa:</span>
          <select value={selectedEmpresa} onChange={e => setSelectedEmpresa(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 bg-white"
            data-testid="empresa-selector">
            <option value="">Seleccionar empresa</option>
            {empresas.map(e => (
              <option key={e.id} value={e.id}>{toTitleCase(e.razon_social)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Available Sessions */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>
      ) : sesiones.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay sesiones disponibles por el momento</p>
        </div>
      ) : (
        groupedEntriesDesc.map(([fecha, daySesiones]) => (
          <div key={fecha} className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {fecha.split('-').reverse().join('-')}
            </h3>
            {daySesiones.map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-semibold text-slate-800">{toTitleCase(s.capacitacion_nombre)}</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      {s.hora_inicio} - {s.hora_fin} | {s.lugar} | {s.modalidad || 'E-Learning'}
                      {s.url_sesion && (
                        <a href={s.url_sesion} target="_blank" rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline">
                          <Video className="w-3.5 h-3.5" />Link<ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-lg font-bold ${s.cupo_disponible <= 5 ? 'text-amber-600' : 'text-teal-600'}`}>
                        {s.cupo_disponible}
                      </p>
                      <p className="text-[10px] text-slate-400">cupos disponibles</p>
                    </div>
                    {selectedEmpresa && s.cupo_disponible > 0 && (
                      <button onClick={() => { setShowInscribir(s); setSelectedWorkers([]); }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                        data-testid={`inscribir-btn-${s.id}`}>
                        <Plus className="w-4 h-4" /> Inscribir
                      </button>
                    )}
                    {!selectedEmpresa && (
                      <p className="text-xs text-amber-500 max-w-[120px] text-center">Seleccione su empresa primero</p>
                    )}
                  </div>
                </div>

                {/* My enrolled workers */}
                {s.mis_inscritos?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Mis trabajadores inscritos ({s.mis_inscritos.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {s.mis_inscritos.map(w => (
                        <div key={w.id} className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
                          <span className="text-indigo-700 font-medium">{toTitleCase(w.nombre)}</span>
                          <span className="text-indigo-400">({w.rut})</span>
                          <button onClick={() => handleCancelar(w.id)} className="text-red-400 hover:text-red-600 ml-1" title="Cancelar">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full transition-all ${s.cupo_disponible <= 5 ? 'bg-amber-500' : 'bg-teal-500'}`}
                      style={{ width: `${((s.cupo_maximo - s.cupo_disponible) / s.cupo_maximo) * 100}%` }} />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 text-right">{s.inscritos}/{fmtCupo(s.cupo_maximo)} inscritos</p>
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* Modal Inscribir Trabajadores */}
      {showInscribir && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowInscribir(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="inscribir-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Inscribir Trabajadores</h2>
                <p className="text-sm text-slate-500">{toTitleCase(showInscribir.capacitacion_nombre)} - {showInscribir.fecha.split('-').reverse().join('-')}</p>
              </div>
              <button onClick={() => setShowInscribir(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-700">Cupos disponibles: <strong>{showInscribir.cupo_disponible}</strong></span>
              {selectedWorkers.length > 0 && (
                <span className="ml-auto text-sm text-indigo-600 font-medium">{selectedWorkers.length} seleccionado(s)</span>
              )}
            </div>

            <div className="overflow-auto flex-1 px-6 py-4">
              {usuarios.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No se encontraron trabajadores para esta empresa</p>
              ) : (
                <div className="space-y-1">
                  {usuarios.map(u => {
                    const isSelected = selectedWorkers.find(w => w.rut === u.rut);
                    const alreadyEnrolled = showInscribir.mis_inscritos?.find(w => w.rut === u.rut);
                    return (
                      <div key={u.id_ext || u.rut}
                        onClick={() => !alreadyEnrolled && toggleWorker(u)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                          alreadyEnrolled ? 'bg-slate-50 opacity-50 cursor-not-allowed' :
                          isSelected ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                        }`}>
                        <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                          alreadyEnrolled ? 'bg-slate-200 border-slate-300' :
                          isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'
                        }`}>
                          {(isSelected || alreadyEnrolled) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 truncate">{toTitleCase(u.nombre)}</p>
                          <p className="text-xs text-slate-400">{u.rut} {alreadyEnrolled ? '(ya inscrito)' : ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200">
              <button onClick={handleInscribir}
                disabled={selectedWorkers.length === 0 || selectedWorkers.length > showInscribir.cupo_disponible}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="confirm-inscripcion-btn">
                Inscribir {selectedWorkers.length} trabajador(es)
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      {/* Modal Confirmacion */}
      {confirmAction && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-slate-700">{confirmAction.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="confirm-cancel-btn">Cancelar</button>
              <button onClick={confirmAction.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-ok-btn">Confirmar</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default PerfilContratista;
